import { Container, Graphics, Text, TextStyle } from "pixi.js";

/**
 * Les deux bornes de la zone d'enregistrement.
 *
 * Deux lignes violettes en travers du roll, posées sur des demi-temps, chacune
 * avec sa capsule dans la règle et son étiquette — « ● DÉBUT M.5 », « ● FIN
 * M.9 ». C'est de là que partira l'enregistrement, et c'est là qu'il s'arrêtera.
 *
 * Le premier appui dans la règle pose le début, le second pose la fin ; ensuite
 * chaque appui reprend la borne la plus proche, et tirer une capsule hors de la
 * règle la jette (voir `PianoRollComponent`). Rien de tout cela ne survit à la
 * séance : c'est un repérage, pas un réglage.
 *
 * # Elles se calent sur le demi-temps, jamais entre deux
 *
 * Le calage est fait en amont, sur le tick : ici on ne dessine que ce qu'on
 * reçoit. C'est ce qui permet à l'étiquette de dire « M.5 » sans arrondi ni
 * approximation — la borne *est* le début de la mesure 5.
 *
 * # Ce qui les sépare ne se dessine pas ici
 *
 * L'entre-deux appartient au calque d'assombrissement (`WorkRange`), qui voile
 * tout ce qui est en dehors de la zone : c'est déjà dire où elle commence et où
 * elle finit, sur toute la largeur du roll. Un trait de plus dans la gouttière
 * répéterait la même chose à voix basse.
 *
 * # Tout se dérive de la largeur de la règle
 *
 * Les lignes traversent toute la largeur, les capsules vivent dans la règle et
 * suivent son ouverture, les étiquettes se posent juste après le panneau. Quand
 * la règle s'ouvre, les bornes s'ouvrent avec elle, d'un seul mouvement.
 * Fermée, les capsules s'effacent et il reste les lignes et leurs noms.
 */

export type RecordEdgeKind = 'start' | 'end';

/** Ce qu'il faut savoir d'une borne pour la dessiner et la nommer. */
type EdgeProps = {
  /** Son tick, calé sur un demi-temps. `null` = elle n'est pas posée. */
  tick: number | null;
  /** Le numéro de la mesure qui la contient. */
  barNumber: number;
  /**
   * Le numéro du temps dans cette mesure, 1-based.
   *
   * La borne se cale sur la plus petite graduation de la règle, qui est le
   * demi-temps : elle tombe donc le plus souvent entre deux barres, et
   * l'étiquette doit le dire. Sur le premier temps d'une mesure elle n'écrit
   * que celle-ci — « M.5 » dit déjà « au début de la mesure 5 », et « M.5.1 »
   * n'ajouterait qu'une précision que personne ne cherche.
   */
  beatNumber: number;
  /** Elle est sur le « et » du temps, à mi-chemin du suivant. */
  offBeat: boolean;
};

type RecordMarkerProps = {
  start: EdgeProps;
  end: EdgeProps;
  fromTick: number;
  toTick: number;
  width: number;
  height: number;
  /** Largeur courante de la règle, animation d'ouverture comprise. */
  rulerWidth: number;
  /** Ouverture de la règle, de 0 à 1 : les capsules n'existent qu'ouverte. */
  openness: number;
  /** La borne que le pointeur est en train de déplacer, s'il y en a une. */
  dragging: RecordEdgeKind | null;
  /**
   * À quel point la borne tenue est sortie de la règle, de 0 à 1.
   *
   * Elle pâlit à mesure, et non d'un coup : le geste de jeter n'a pas de seuil
   * qu'on franchit à l'aveugle, il a une pente qu'on remonte tant qu'on n'a pas
   * relâché. À 1 elle est au plancher, et le relâchement la jette.
   */
  discardProgress: number;
  /**
   * Abscisse du pointeur pendant qu'il tient une borne, `null` sinon.
   *
   * La capsule la suit, au lieu de rester plantée dans sa colonne. C'est ce qui
   * fait comprendre qu'on la *tient* : une poignée qui ne bouge pas dans l'axe
   * où le doigt la tire n'a pas l'air d'une poignée, et rien n'annoncerait
   * alors qu'on peut la sortir de la règle pour la jeter.
   */
  dragX: number | null;
};

const ACCENT = 0xa78bfa;
const ACCENT_BRIGHT = 0xc4b5fd;
const NEUTRAL = 0xcbd5e1;

const LINE_THICKNESS = 2;
const LINE_ALPHA = 0.75;
const LINE_ALPHA_DRAGGING = 1;
/** Ce qu'il reste d'une borne au bout de la pente, juste avant d'être jetée. */
const DISCARD_ALPHA_FLOOR = 0.25;

/** La capsule, poignée d'une borne dans la règle. */
const HANDLE_WIDTH = 32;
const HANDLE_HEIGHT = 8;
/** Bord droit de la capsule, mesuré depuis celui du panneau. */
const HANDLE_FROM_RIGHT = 22;

/**
 * Centre de la capsule au repos, mesuré depuis le bord droit du panneau.
 *
 * Exporté parce que c'est de là que part le geste de sortie : la pente
 * d'effacement se compte depuis la place de la capsule, pas depuis le bord de
 * l'écran (voir `PianoRollComponent`).
 */
export const RECORD_HANDLE_INSET = HANDLE_FROM_RIGHT + HANDLE_WIDTH / 2;

/** Entre le bord du panneau et l'étiquette. */
const LABEL_INSET = 20;
/** Hauteur de l'étiquette au-dessus de sa ligne. */
const LABEL_ABOVE = 16;
/**
 * En deçà de cet écart, l'étiquette du début passe **sous** sa ligne.
 *
 * La fin est la borne du haut — le temps monte : quand les deux se serrent,
 * c'est le nom du début qui va buter dans la ligne de la fin, et c'est donc lui
 * qui cède.
 */
const LABEL_MIN_GAP = 30;
const LABEL_FONT_SIZE = 10;
const LABEL_LETTER_SPACING = 2.4;
/** Le numéro de mesure, en retrait derrière le nom de la borne. */
const MEASURE_ALPHA = 0.7;
/** Le point qui ouvre l'étiquette. */
const DOT_RADIUS = 2.5;
const DOT_GAP = 10;

/** Les pièces d'une borne, créées une fois et repositionnées à chaque image. */
type EdgeSlot = {
  line: Graphics;
  handle: Graphics;
  dot: Graphics;
  kindText: Text;
  measureText: Text;
  /** L'étiquette écrite en ce moment, pour ne pas la reconstruire pour rien. */
  measureLabel: string;
};

export class RecordMarker {
  private container: Container;
  private startSlot: EdgeSlot;
  private endSlot: EdgeSlot;

  private previousProps?: RecordMarkerProps;

  constructor(private stage: Container) {
    this.container = new Container();
    this.stage.addChild(this.container);

    this.startSlot = this.createSlot("DÉBUT");
    this.endSlot = this.createSlot("FIN");
  }

  /** Repeindre la prochaine image même si les props n'ont pas bougé. */
  invalidate(): void {
    this.previousProps = undefined;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  draw(props: RecordMarkerProps): void {
    if (this.shouldSkip(props)) {
      return;
    }

    this.previousProps = {
      ...props,
      start: { ...props.start },
      end: { ...props.end },
    };

    const { start, end, fromTick, toTick, height, dragging } = props;
    const dim = 1 - props.discardProgress * (1 - DISCARD_ALPHA_FLOOR);
    const tickRange = toTick - fromTick;

    if (tickRange <= 0 || height <= 0) {
      this.hideSlot(this.startSlot);
      this.hideSlot(this.endSlot);

      return;
    }

    const scale = height / tickRange;
    const startY = start.tick === null ? null : height - (start.tick - fromTick) * scale;
    const endY = end.tick === null ? null : height - (end.tick - fromTick) * scale;

    // Le début est la borne du bas. Serrés, c'est son étiquette qui passe de
    // l'autre côté de sa ligne.
    const tight = startY !== null && endY !== null && startY - endY < LABEL_MIN_GAP;

    this.drawEdge(this.startSlot, start, startY, props, dragging === 'start', tight, dim);
    this.drawEdge(this.endSlot, end, endY, props, dragging === 'end', false, dim);
  }

  private drawEdge(
    slot: EdgeSlot,
    edge: EdgeProps,
    y: number | null,
    props: RecordMarkerProps,
    dragging: boolean,
    labelBelow: boolean,
    dim: number,
  ): void {
    // Seule la borne en main peut être en train de sortir ; l'autre garde sa
    // pleine présence, elle n'est pas concernée par le geste.
    const fade = dragging ? dim : 1;

    // Hors de la fenêtre visible il n'y a rien à montrer : une borne est une
    // position dans le morceau, pas un bord d'écran vers lequel pointer.
    if (edge.tick === null || y === null || y < 0 || y > props.height) {
      this.hideSlot(slot);

      return;
    }

    slot.line.x = 0;
    slot.line.y = y - LINE_THICKNESS / 2;
    slot.line.scale.set(props.width, LINE_THICKNESS);
    slot.line.alpha = (dragging ? LINE_ALPHA_DRAGGING : LINE_ALPHA) * fade;
    slot.line.visible = true;

    // Centrée sous le doigt quand il la tire, jamais à gauche de sa place :
    // le panneau est son chez-elle, elle n'en sort que vers le roll.
    const handleHome = props.rulerWidth - HANDLE_FROM_RIGHT - HANDLE_WIDTH;

    slot.handle.x = dragging && props.dragX !== null
      ? Math.max(handleHome, props.dragX - HANDLE_WIDTH / 2)
      : handleHome;
    slot.handle.y = y - HANDLE_HEIGHT / 2;
    slot.handle.alpha = props.openness * fade;
    slot.handle.visible = props.openness > 0.01;

    const labelX = props.rulerWidth + LABEL_INSET;
    // Retenue sous le bord haut : une borne posée sur la première mesure
    // visible aurait son nom coupé par le haut du roll.
    const labelY = labelBelow
      ? y + LABEL_ABOVE
      : Math.max(LABEL_FONT_SIZE, y - LABEL_ABOVE);

    slot.dot.x = labelX + DOT_RADIUS;
    slot.dot.y = labelY;
    slot.dot.alpha = fade;
    slot.dot.visible = true;

    slot.kindText.x = labelX + DOT_RADIUS * 2 + DOT_GAP;
    slot.kindText.y = labelY;
    slot.kindText.alpha = fade;
    slot.kindText.visible = true;

    const measureLabel = labelOf(edge);

    if (measureLabel !== slot.measureLabel) {
      slot.measureText.text = measureLabel;
      slot.measureLabel = measureLabel;
    }

    slot.measureText.x = slot.kindText.x + slot.kindText.width + DOT_GAP;
    slot.measureText.y = labelY;
    slot.measureText.alpha = MEASURE_ALPHA * fade;
    slot.measureText.visible = true;
  }

  private hideSlot(slot: EdgeSlot): void {
    slot.line.visible = false;
    slot.handle.visible = false;
    slot.dot.visible = false;
    slot.kindText.visible = false;
    slot.measureText.visible = false;
  }

  private createSlot(kind: string): EdgeSlot {
    const line = new Graphics();
    line.rect(0, 0, 1, 1);
    line.fill({ color: ACCENT_BRIGHT, alpha: 1 });
    line.visible = false;
    this.container.addChild(line);

    const handle = new Graphics();
    handle.roundRect(0, 0, HANDLE_WIDTH, HANDLE_HEIGHT, HANDLE_HEIGHT / 2);
    handle.fill({ color: ACCENT_BRIGHT, alpha: 1 });
    handle.visible = false;
    this.container.addChild(handle);

    const dot = new Graphics();
    dot.circle(0, 0, DOT_RADIUS);
    dot.fill({ color: ACCENT, alpha: 1 });
    dot.visible = false;
    this.container.addChild(dot);

    const kindText = new Text({
      text: kind,
      style: new TextStyle({
        fontFamily: "system-ui, sans-serif",
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "600",
        letterSpacing: LABEL_LETTER_SPACING,
        fill: ACCENT_BRIGHT,
      }),
    });
    kindText.anchor.set(0, 0.5);
    kindText.visible = false;
    this.container.addChild(kindText);

    const measureText = new Text({
      text: "",
      style: new TextStyle({
        fontFamily: "system-ui, sans-serif",
        fontSize: LABEL_FONT_SIZE,
        letterSpacing: LABEL_LETTER_SPACING,
        fill: NEUTRAL,
      }),
    });
    measureText.anchor.set(0, 0.5);
    measureText.alpha = MEASURE_ALPHA;
    measureText.visible = false;
    this.container.addChild(measureText);

    return { line, handle, dot, kindText, measureText, measureLabel: "" };
  }

  private shouldSkip(props: RecordMarkerProps): boolean {
    const prev = this.previousProps;

    if (!prev) {
      return false;
    }

    return sameEdge(prev.start, props.start)
      && sameEdge(prev.end, props.end)
      && prev.fromTick === props.fromTick
      && prev.toTick === props.toTick
      && prev.width === props.width
      && prev.height === props.height
      && prev.rulerWidth === props.rulerWidth
      && prev.openness === props.openness
      && prev.dragging === props.dragging
      && prev.discardProgress === props.discardProgress
      && prev.dragX === props.dragX;
  }
}

/**
 * Ce que dit l'étiquette : « M.5 », « M.5.3 », « M.5.3½ ».
 *
 * Le ½ plutôt qu'un troisième nombre : « M.5.3.2 » se lit comme une adresse,
 * alors que le demi-temps est une moitié de temps — c'est le « et » du 3, et
 * c'est ainsi qu'un pianiste le compte.
 */
function labelOf(edge: EdgeProps): string {
  if (edge.beatNumber === 1 && !edge.offBeat) {
    return `M.${edge.barNumber}`;
  }

  return `M.${edge.barNumber}.${edge.beatNumber}${edge.offBeat ? "½" : ""}`;
}

function sameEdge(a: EdgeProps, b: EdgeProps): boolean {
  return a.tick === b.tick
    && a.barNumber === b.barNumber
    && a.beatNumber === b.beatNumber
    && a.offBeat === b.offBeat;
}
